import * as AngularCsv from 'angular-csv-ext/dist/Angular-csv';
import { format } from 'date-fns';
import { Observable, Subject, of } from 'rxjs';
import { debounceTime, takeUntil, tap } from 'rxjs/operators';

import { Component, Inject, OnDestroy, OnInit, ViewChild, ChangeDetectorRef } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { MatSelectChange } from '@angular/material/select';
import { Sort, SortDirection } from '@angular/material/sort';

import { MxEventName } from '@axon-enterprise/track';
import { ISnListQueryParameters, ISnListResponse, SortParameters } from '@lib/api';
import { SnCurrentUser } from '@lib/auth';
import { snZoomIn } from '@lib/common/animations';
import { MixpanelService } from '@lib/mixpanel';
import { t } from '@lib/translation';
import { SnActionConfig } from '@lib/ui/actions';
import { SnDataListComponent } from '@lib/ui/data-list';
import { SnAbilityService } from 'libs/ability/src/lib/services';

import { LprHotListAPI, SnLpr, SnLprObject } from '../../api';
import { AddLprhotlistFormComponent } from '../add-lprhotlist-form/add-lprhotlist-form.component';

@Component({
  selector: 'fs-lpr-hotlists-plates-dialog',
  templateUrl: './lpr-hotlists-plates-dialog.component.html',
  styleUrls: ['./lpr-hotlists-plates-dialog.component.scss'],
  animations: [snZoomIn],
})
export class LprHotlistsPlatesDialogComponent implements OnDestroy, OnInit {
  @ViewChild('listElement', { static: true })
  public readonly list: SnDataListComponent<SnLpr>;
  public readonly loadData = this._loadData.bind(this);

  public mode: string = 'add';
  public lprObjects: SnLprObject[] = [];

  public currentUserID: string;
  public columns: any[];
  public readonly actions: SnActionConfig<SnLpr>[] = this._genActions();
  public readonly searchControl: UntypedFormControl = new UntypedFormControl();

  public _querySearch: string;
  public columnNames: any;
  public isLoading: boolean = false;
  public columnHideInList: boolean;
  public canAddPlate: boolean;
  public activeHotlist: SnLpr;

  public sort = 'plate';
  public sortDirection: SortDirection = 'asc';

  private readonly _destroy$ = new Subject<void>();

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: {
      hotlists: SnLpr[];
      hotlist?: SnLpr;
    },
    private readonly _lprHotlistAPI: LprHotListAPI,
    private readonly _dialog: MatDialog,
    private readonly _changeDetector: ChangeDetectorRef,
    private readonly _currentUser: SnCurrentUser,
    private readonly _ability: SnAbilityService,
    private readonly _mixpanelService: MixpanelService,
  ) {
    this.activeHotlist = this.data.hotlist ? this.data.hotlist : this.data.hotlists[4];
    if (this.activeHotlist) {
      this.canAddPlate =
        this.activeHotlist.dolpUsers.editor?.includes(this._currentUser.id) ||
        this.activeHotlist.dolpUsers.admin?.includes(this._currentUser.id) ||
        this._ability.can({ subject: 'lpr/lprhotlistobject', action: ['add'] });
      this.columns = this._getColumns();
    }
  }

  public ngOnInit(): void {
    if (this.activeHotlist) {
      this.currentUserID = this._currentUser.id;
      this.columnNames = this.columns.filter((c) => !c.hideInList).map((column) => column.name);
      this.searchControl.valueChanges
        .pipe(debounceTime(300), takeUntil(this._destroy$))
        .subscribe((v) => {
          this._querySearch = v;
          this._reloadData();
        });
    }
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public downloadPlates(hotlistID: string, hotlistName: string): void {
    this._exportLprHotlist(hotlistID, hotlistName);
  }

  public onSortChange(sort: Sort): void {
    this.sort = sort.active;
    this.sortDirection = sort.direction;

    this._reloadData();
  }

  public clear(): void {
    this.searchControl.reset(null);
  }

  public addPlate(): void {
    this.mode = 'add';
    this._dialog
      .open(AddLprhotlistFormComponent, {
        data: {
          hotlist: this.activeHotlist,
          mode: this.mode,
          lprObjects: this.lprObjects,
        },
        maxHeight: '85vh',
      })
      .afterClosed()
      .pipe(takeUntil(this._destroy$))
      .subscribe((results) => {
        if (results) {
          this._mixpanelService.trackMetric(MxEventName.AddPlateHotAf, {});
          this.list.dataSource.insertItemToStart(results);
        }
      });
  }

  public trackColumn(index: number, item: any): string {
    return item.name;
  }

  public toggleColumn(index: number): void {
    this.columns[index].hideInList = !this.columns[index].hideInList;
    const newColumn = this.columns.filter((col) => !col.hideInList);
    this.columnNames = newColumn.map((col) => col.name);
    this._changeDetector.detectChanges();
  }

  public toggleAllColumns(event: any): void {
    this.columns = this.columns.map((col) => {
      col.hideInList = !event.checked;
      return col;
    });
    const newColumn = this.columns.filter((col) => !col.hideInList);
    this.columnNames = newColumn.map((col) => col.name);
    this._changeDetector.detectChanges();
  }

  public resetColumns(): void {
    this.columns = this.columns.map((col) => {
      col.hideInList = col.default;
      return col;
    });
    const newColumn = this.columns.filter((col) => !col.hideInList);
    this.columnNames = newColumn.map((col) => col.name);
    this._changeDetector.detectChanges();
  }

  public onHotlistChange(event: MatSelectChange): void {
    this.activeHotlist = event.value;
    this._reloadData();
    this._changeDetector.detectChanges();
  }

  private _getColumns(): any {
    const addColumns =
      this.activeHotlist.properties.fields?.map((field) => ({
        name: field.fieldName,
        hideInList: field.hideInList,
      })) || [];

    return [
      { name: 'plate', hideInList: false, default: false },
      { name: 'plateState', hideInList: false, default: false },
      { name: 'reason', hideInList: false, default: false },
      ...addColumns,
      { name: 'createdBy', hideInList: false, default: false },
      { name: 'createdAt', hideInList: false, default: true },
      { name: 'expiresAt', hideInList: false, default: false },
      { name: 'actions', hideInList: false, default: false },
    ];
  }

  private _loadData(params: ISnListQueryParameters): Observable<ISnListResponse<SnLprObject>> {
    params.search = this._querySearch;
    if (!this.activeHotlist) {
      return of({
        results: [],
        totalPages: 0,
        count: 0,
      });
    }
    return this._lprHotlistAPI
      .getGroupsLprPlates(this._prepareLoadQueryParameters(params))
      .pipe(tap((response) => (this.lprObjects = response.results)));
  }

  private _reloadData(): void {
    this.list.reference.reload();
  }

  private _deletePlate(item: SnLpr): void {
    this._lprHotlistAPI
      .removePlateFromLprObject(item.id)
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => {
        this.list.dataSource.deleteItem(item);
      });
  }

  private _editPlate(item: SnLpr): void {
    this.mode = 'edit';
    this._dialog
      .open(AddLprhotlistFormComponent, {
        data: {
          hotlist: this.activeHotlist,
          plate: item,
          mode: this.mode,
          lprObjects: this.lprObjects,
        },
        maxHeight: '85vh',
      })
      .afterClosed()
      .pipe(takeUntil(this._destroy$))
      .subscribe((results) => {
        if (results) {
          this.list.dataSource.updateItem(results);
        }
      });
  }

  private _prepareLoadQueryParameters(params: ISnListQueryParameters): ISnListQueryParameters {
    return {
      lpr_hotlist: this.activeHotlist.id,
      ordering: new SortParameters({
        active: this.sort,
        direction: this.sortDirection,
      }).convertedActive,
      search: '',
      ...params,
    };
  }

  private _exportLprHotlist(hotlistID: string, hotlistName: string): any {
    this.isLoading = true;
    this._lprHotlistAPI
      .getGroupsLprPlates({ lpr_hotlist: hotlistID, search: this._querySearch, pageSize: 'all' })
      .pipe(takeUntil(this._destroy$))
      .subscribe((response) => {
        this.isLoading = false;
        const data = response.results.map((res: SnLprObject) => {
          const plate = {
            [t('vehicleIntelligence.hotlist.plateName')]: res.plate,
            [t('common.createdAt')]: format(res.createAt, 'Pp'),
            [t('settings.demoPanel.alprAlert.reason')]: res.properties['reason'],
            [t('common.creator')]: res.createdBy?.fullName,
          };

          this.activeHotlist.properties.fields.forEach((field) => {
            plate[field.fieldName] = '';
            if (res.properties.hotlistData) {
              plate[field.fieldName] = res.properties.hotlistData[field.fieldName];
            }
          });
          return plate;
        });
        return new AngularCsv.AngularCsv(data, t('vehicleIntelligence.hotlist.platesInName', { name: hotlistName }), {
          headers: Object.keys(data[0]),
        });
      });
  }

  private _genActions(): SnActionConfig<SnLpr>[] {
    return [
      {
        label: t('common.edit'),
        icon: {
          fontSet: 'fal',
          fontIcon: 'fa-pen',
        },
        click: (item) => {
          this._editPlate(item);
        },
      },
      {
        label: t('common.delete'),
        icon: {
          fontSet: 'fal',
          fontIcon: 'fa-trash-alt',
        },
        color: 'warn',
        click: (item) => {
          this._deletePlate(item);
        },
      },
    ];
  }
}
